import React, { useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ChatHeader } from '@atlassian/conversation-assistant-ui-components/ui/ChatHeaderRefresh';
import {
	type ThreadSource,
	extractSourcesFromThread,
} from '@atlassian/rovo-navigation/home-threads-service';
import { RecommendedSpacesEmptyState } from '@atlassian/rovo-spaces/ui/RecommendedSpacesEmptyState';
import { RecommendedSpacesSourcesTrigger } from '@atlassian/rovo-spaces/ui/RecommendedSpacesSourcesTrigger';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';

import type { RecommendedSpacesPanelProps } from './RecommendedSpacesPanel';
import { RecommendedSpacesPanelSkeleton } from './RecommendedSpacesPanelSkeleton';

const messages = defineMessages({
	panelTitle: {
		id: 'conversation-assistant.recommended-spaces-panel.RecommendedSpacesPanelRenderer.panelTitle',
		defaultMessage: 'Recommended Rovo spaces',
		description: 'Title of the recommended spaces panel shown in fullscreen mode',
	},
	panelSubtitle: {
		id: 'conversation-assistant.recommended-spaces-panel.RecommendedSpacesPanelRenderer.panelSubtitle',
		defaultMessage:
			'Based on your recent activity, these suggested Rovo spaces can help you group related documents and keep your project organized.',
		description: 'Subtitle of the recommended spaces panel describing the recommendations',
	},
	createSpace: {
		id: 'conversation-assistant.recommended-spaces-panel.RecommendedSpacesPanelRenderer.createSpace',
		defaultMessage: 'Create space',
		description: 'Label for the button that initiates creating a new space from a recommendation',
	},
	newSpace: {
		id: 'conversation-assistant.recommended-spaces-panel.RecommendedSpacesPanelRenderer.newSpace',
		defaultMessage: 'New space',
		description:
			'Label for the primary header button that opens the create-space modal with no pre-filled data',
	},
});

const styles = cssMap({
	wrapper: {
		inlineSize: '100%',
		blockSize: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		minHeight: '0px',
	},
	headerWrapper: {
		flexShrink: 0,
	},
	scrollableContent: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		overflowY: 'auto',
		overflowX: 'hidden',
		minHeight: '0px',
	},
	emptyStateContainer: {
		display: 'flex',
		flexGrow: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	panel: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		inlineSize: '100%',
		minBlockSize: '100%',
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
	},
	panelFullscreen: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		inlineSize: '100%',
		minBlockSize: '100%',
		paddingBlockStart: token('space.500'),
		paddingBlockEnd: token('space.500'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.600'),
	},
	subtitle: {
		marginBlockEnd: token('space.200'),
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.200'),
		alignSelf: 'stretch',
		borderRadius: token('radius.xlarge'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		marginBlockEnd: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
	},
});

/**
 * Shared renderer that takes pre-fetched threads and renders the recommendation
 * cards. Both the GraphQL and REST data-fetching wrappers delegate to this.
 */
export const RecommendedSpacesPanelRenderer = ({
	threads,
	onCreateSpace,
	chatRefreshHeader,
	isLoading = false,
}: {
	// Structural shape covering both the Relay `convoai_homeThreads` node and the REST
	// `RecommendedThreadNode`; only `id`/`title`/`sources` are consumed here.
	threads: ReadonlyArray<{
		id: string;
		title: string;
		sources: readonly (ThreadSource | null)[] | null;
	}>;
	onCreateSpace: RecommendedSpacesPanelProps['onCreateSpace'];
	chatRefreshHeader?: React.ReactNode;
	isLoading?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fullScreen, elements } = useUIConfig();
	const { closeChat } = useGlobalConfig();
	const { sendAnalyticsEvent } = useAnalytics();
	const viewedRecommendationIdsRef = useRef<string | null>(null);
	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const shouldShowCloseButton = !!elements?.closeButton;

	useEffect(() => {
		if (isLoading) {
			return;
		}
		const recommendationIds = threads.map((thread) => thread.id).join(',');
		if (viewedRecommendationIdsRef.current === recommendationIds) {
			return;
		}
		viewedRecommendationIdsRef.current = recommendationIds;
		sendAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'recommendedSpaces',
			attributes: {
				recommendationCount: threads.length,
			},
		});
	}, [isLoading, sendAnalyticsEvent, threads]);

	const handleCreateRecommendedSpace = (
		title: string,
		spaceSources: Parameters<RecommendedSpacesPanelProps['onCreateSpace']>[1],
	) => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'recommendedSpaceCreateSpaceButton',
		});
		onCreateSpace(title, spaceSources);
	};

	const newSpaceButton = (
		<Button
			appearance="primary"
			iconBefore={AddIcon}
			onClick={() => onCreateSpace('', [])}
			testId="recommended-spaces-panel-new-space-button"
		>
			{formatMessage(messages.newSpace)}
		</Button>
	);

	return (
		<Stack xcss={styles.wrapper}>
			{!fullScreen && chatRefreshHeader && (
				<Box xcss={styles.headerWrapper}>{chatRefreshHeader}</Box>
			)}
			<Box xcss={styles.scrollableContent}>
				{fullScreen && (isLoading || threads.length > 0) && (
					<ChatHeader
						agentTitleComponent={
							<Stack space="space.050">
								<Heading size="medium">{formatMessage(messages.panelTitle)}</Heading>
								<Text size="small" color="color.text.subtlest">
									{formatMessage(messages.panelSubtitle)}
								</Text>
							</Stack>
						}
						moreMenuComponent={newSpaceButton}
						onBackClick={() => setActiveMenu('none')}
						onCloseClick={shouldShowCloseButton ? closeChat : undefined}
					/>
				)}
				<Box xcss={fullScreen ? styles.panelFullscreen : styles.panel}>
					{!fullScreen && threads.length > 0 && (
						<Box xcss={styles.subtitle}>
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.panelSubtitle)}
							</Text>
						</Box>
					)}
					{isLoading ? (
						<RecommendedSpacesPanelSkeleton />
					) : threads.length === 0 ? (
						<Box
							xcss={styles.emptyStateContainer}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- percentage layout offset not expressible as a token
							style={{ paddingBlockEnd: fullScreen ? '33%' : '66%' }}
						>
							<RecommendedSpacesEmptyState onCreateSpace={() => onCreateSpace('', [])} />
						</Box>
					) : (
						threads.map((thread) => {
							const sources = (thread.sources ?? []).filter((s): s is ThreadSource => s != null);
							const { sourceUrls, spaceSources } = extractSourcesFromThread(sources);
							const sourceCount = sources.length;

							// TODO ROVOFE-2869: render an emoji/icon next to the title once the backend
							// surfaces a recommendation icon for each thread.
							return (
								<Box key={thread.id} xcss={styles.card}>
									<Stack space="space.200" grow="fill">
										<Text weight="bold">{thread.title}</Text>
										<Flex
											direction="row"
											gap="space.100"
											alignItems="center"
											justifyContent={fullScreen ? 'start' : 'space-between'}
										>
											<Button
												appearance="default"
												onClick={() => handleCreateRecommendedSpace(thread.title, spaceSources)}
											>
												{formatMessage(messages.createSpace)}
											</Button>
											{sourceCount > 0 && (
												<RecommendedSpacesSourcesTrigger
													sources={spaceSources}
													sourceUrls={sourceUrls}
													sourceCount={sourceCount}
													spaceTitle={thread.title}
												/>
											)}
										</Flex>
									</Stack>
								</Box>
							);
						})
					)}
				</Box>
			</Box>
		</Stack>
	);
};
